import React, { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Collapse, FormText } from 'reactstrap'
import { useField } from 'formik'
import PT from 'prop-types'
import _ from 'lodash'

import { Hide, Show } from '~/components/commons'
import styled from 'styled-components'
import { PlusCircle as IconPlusCircle } from 'react-feather'

VariablesDropdown.propTypes = {
  onChange: PT.func.isRequired,
}

function VariablesDropdown({ onChange }) {
  const variablesList = [
    { label: 'Location name', value: '{{local_name}}' },
    { label: 'Location city', value: '{{local_city}}' },
    { label: 'Location area', value: '{{local_area}}' },
    { label: 'Location state', value: '{{local_state}}' },
    { label: 'Location code', value: '{{store_code}}' },
    { label: 'Location hours', value: '{{store_hours}}' },
  ]

  const [open, setOpen] = React.useState(false)
  return (
    <>
      <VariableButton onClick={() => setOpen(value => !value)}>
        <IconPlusCircle
          size={10}
          style={{ color: '#fff', marginRight: '5px' }}
        />
        <FormattedMessage id="Variables" />
        <Show condition={open}>
          <VariableList>
            {variablesList.map((variable, i) => (
              <VariableItem key={i} onClick={() => onChange(variable)}>
                <FormattedMessage id={variable.label} />
              </VariableItem>
            ))}
          </VariableList>
        </Show>
      </VariableButton>
    </>
  )
}

function Field(
  {
    render: Render,
    name,
    label,
    helperText = '',
    className,
    onChange,
    errorExtractor,
    required = false,
    enableBlurAction = true,
    showHelperText = true,
    enableVariables,
    ...props
  },
  ref
) {
  const [
    { onChange: handleFormikChange, onBlur, ...inputProps },
    meta,
    helpers,
  ] = useField({
    name,
    type: props.type || 'text',
  })

  const error = useMemo(
    () => (_.isObject(meta.error) ? errorExtractor(meta.error) : meta.error),
    [meta.error, errorExtractor]
  )

  const intl = useIntl()

  return (
    <div style={{ width: '100%' }}>
      <label
        className="font-small-3 d-flex justify-content-between"
        htmlFor={name}
      >
        <div className="d-flex">
          {label && <FormattedMessage id={label} />}{' '}
          <Show condition={required && _.isEmpty(error)}> *</Show>
          <Show condition={!required && _.isEmpty(error)}>
            (<FormattedMessage id="optional" />)
          </Show>
          <Hide condition={_.isEmpty(error)}>
            <strong className="text-danger">{` - ${
              error &&
              intl.formatMessage({
                id: error,
              })
            }`}</strong>
          </Hide>
        </div>
        <Show condition={enableVariables}>
          <VariablesDropdown
            onChange={variable =>
              helpers.setValue(`${inputProps.value}${variable.value}`)
            }
          />
        </Show>
      </label>
      <Render
        ref={ref}
        id={name}
        className={`${className} ${inputProps.className}`}
        onChange={event =>
          _.isFunction(onChange)
            ? onChange(event, helpers)
            : handleFormikChange(event)
        }
        invalid={!_.isEmpty(error)}
        {...(enableBlurAction && { onBlur })}
        {...inputProps}
        {...props}
      />
      <Collapse isOpen={!!helperText && showHelperText}>
        <FormText className="text-bold-500">
          <Show condition={!!helperText}>
            <FormattedMessage id={helperText} />
          </Show>
        </FormText>
      </Collapse>
    </div>
  )
}

const VariableButton = styled.div`
  background-color: #0091ff;
  height: 16px;
  font-size: 10px;
  color: #fff;
  border-radius: 8px 8px 0px 0px;
  line-height: 10px;
  font-family: 'Montserrat';
  margin: 2px 0 0 -2px;
  display: flex;
  align-items: center;
  padding: 0 12px;
  margin-right: 10px;
  cursor: pointer;
  position: relative;
  overflow: visible;
`

const VariableList = styled.ul`
  position: absolute;
  width: 100%;
  left: 0;
  top: 18px;
  z-index: 100;
  padding: 0;
  box-shadow: 0px 4px 8px 2px rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  background-color: #fff;
`

const VariableItem = styled.li`
  z-index: 1000;
  list-style: none;
  padding: 2px 5px;
  color: #565656;
  user-select: none;

  &:hover {
    background: rgba(0, 145, 255, 0.7);
    color: #fff;
  }
`

export default React.forwardRef(Field)
